import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { MaskedLine, Reveal, SectionHeading, GoldButton, GhostButton } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import EnquiryForm from "@/components/EnquiryForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { IMAGES, PHONE, PHONE_LINK, SERVICES, STEPS, WHY_CHOOSE, FAQS, HERO_CHECKLIST } from "@/data/site";

const Home = () => {
  const heroRef = useRef(null);
  const bandRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const { scrollYProgress: bandProgress } = useScroll({ target: bandRef, offset: ["start end", "end start"] });
  const bandY = useTransform(bandProgress, [0, 1], ["-12%", "12%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[100svh] items-end overflow-hidden" data-testid="home-hero">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          <img src={IMAGES.hero} alt="Premium luxury vehicle in a dimly lit studio" className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/35 to-obsidian" />
        <motion.div style={{ opacity: contentOpacity }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 md:px-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-gold" data-testid="hero-kicker">
            <MaskedLine delay={0.15}>Sage Auto Group · Accident Management · UK</MaskedLine>
          </p>
          <h1 className="mt-8 font-serif font-medium leading-[1.04] tracking-tight text-white" data-testid="hero-headline">
            <MaskedLine delay={0.3} innerClassName="text-4xl sm:text-6xl lg:text-7xl">
              Had an Accident?
            </MaskedLine>
            <MaskedLine delay={0.45} innerClassName="text-4xl italic sm:text-6xl lg:text-7xl">
              <span className="text-gold-gradient">Let Sage Auto Group</span>
            </MaskedLine>
            <MaskedLine delay={0.6} innerClassName="text-4xl italic sm:text-6xl lg:text-7xl">
              <span className="text-gold-gradient">Handle the Rest.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.85}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg" data-testid="hero-subheading">
              From recovery and replacement vehicles to insurance claims and personal injury support,
              we manage the entire process so you don't have to.
            </p>
          </MaskedLine>
          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 grid max-w-2xl grid-cols-1 gap-x-10 gap-y-3 sm:grid-cols-2"
            data-testid="hero-checklist"
          >
            {HERO_CHECKLIST.map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm text-zinc-200">
                <Check className="h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
                {item}
              </li>
            ))}
          </motion.ul>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-8 font-mono text-[11px] uppercase tracking-[0.35em] text-gold"
            data-testid="hero-tagline"
          >
            One Call. One Team. Complete Peace of Mind.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <GoldButton href={PHONE_LINK} testId="hero-call-now-button">
              <Phone className="h-4 w-4" strokeWidth={2} /> Call Now · {PHONE}
            </GoldButton>
            <GhostButton href="/contact" testId="hero-request-assistance-button">
              Request Assistance <ArrowRight className="h-4 w-4" />
            </GhostButton>
          </motion.div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 right-8 hidden items-center gap-3 md:flex"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Scroll</span>
          <span className="h-px w-16 bg-gradient-to-r from-gold to-transparent" />
        </motion.div>
      </section>

      <Marquee />

      {/* MANIFESTO / HOW IT WORKS SUMMARY */}
      <section className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40" data-testid="home-manifesto">
        <SectionHeading
          kicker="The Sage Standard"
          title={<>One call. <span className="italic text-gold-gradient">One trusted team.</span></>}
          description="From the moment of impact to the day your claim closes, a single dedicated specialist coordinates recovery, storage, your replacement vehicle, repairs and every insurer conversation — through one trusted nationwide network."
        />
        <div className="mt-20 space-y-0 border-t border-white/10">
          {STEPS.map((step, i) => (
            <Reveal key={step.number} delay={i * 0.05}>
              <Link
                to="/how-it-works"
                data-testid={`manifesto-step-${step.number}`}
                className="group grid grid-cols-[auto_1fr] items-baseline gap-6 border-b border-white/10 py-8 transition-colors duration-300 hover:bg-white/[0.02] md:grid-cols-[120px_1fr_auto] md:gap-12 md:px-6"
              >
                <span className="font-mono text-sm text-gold/80 md:text-base">{step.number}</span>
                <div>
                  <h3 className="font-serif text-2xl text-white transition-colors duration-300 group-hover:text-gold md:text-3xl">
                    {step.title}
                  </h3>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-500 md:text-base">{step.text}</p>
                </div>
                <ArrowUpRight className="hidden h-6 w-6 text-zinc-600 transition-[color,transform] duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-gold md:block" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-carbon py-28 md:py-40" data-testid="home-services">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
            <SectionHeading
              kicker="What We Do"
              title="Every detail. Handled."
              description="Seven specialist services, one seamless experience — delivered through our trusted network of recovery operators, repairers, hire providers and legal professionals."
            />
            <Reveal delay={0.2}>
              <Link
                to="/services"
                data-testid="services-view-all-link"
                className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-gold"
              >
                All Services
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-12">
            {SERVICES.map((service, i) => {
              const Icon = service.icon;
              const wide = i === 0 || i === 5;
              return (
                <Reveal key={service.title} delay={i * 0.06} className={wide ? "md:col-span-8" : "md:col-span-4"}>
                  <Link
                    to="/services"
                    data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group flex h-full flex-col justify-between rounded-2xl border border-white/10 bg-graphite/60 p-8 transition-[border-color,background-color,transform] duration-500 hover:-translate-y-1 hover:border-gold/40 hover:bg-graphite md:p-10"
                  >
                    <Icon className="h-7 w-7 text-gold" strokeWidth={1.25} />
                    <div className="mt-12">
                      <h3 className="font-serif text-2xl text-white md:text-[26px]">{service.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-zinc-500">{service.description}</p>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARALLAX BAND */}
      <section ref={bandRef} className="relative flex min-h-[70svh] items-center overflow-hidden" data-testid="home-parallax-band">
        <motion.div style={{ y: bandY }} className="absolute inset-0 scale-125">
          <img src={IMAGES.night} alt="Premium car driving at night" className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 py-28 md:px-10">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold">First Notification → Final Resolution</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-4xl font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl lg:text-6xl">
              You make one call. <span className="italic text-gold-gradient">We carry it from there.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12">
              <GoldButton href={PHONE_LINK} testId="band-call-button">
                <Phone className="h-4 w-4" strokeWidth={2} /> {PHONE}
              </GoldButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40" data-testid="home-why-choose">
        <SectionHeading
          kicker="Why Choose Sage Auto Group"
          title="Built on trust. Run like clockwork."
        />
        <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {WHY_CHOOSE.map((item, i) => {
            const Icon = item.icon;
            return (
              <Reveal key={item.title} delay={i * 0.06} className="bg-obsidian">
                <div
                  data-testid={`why-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group h-full p-8 transition-colors duration-500 hover:bg-graphite md:p-10"
                >
                  <Icon className="h-6 w-6 text-gold" strokeWidth={1.25} />
                  <h3 className="mt-8 font-serif text-xl text-white">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-500">{item.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="bg-carbon py-28 md:py-40" data-testid="home-faq-preview">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:px-10 lg:grid-cols-2">
          <div>
            <SectionHeading
              kicker="Questions"
              title="The answers you need, before you even call."
              description="Everything most drivers want to know after an accident. For anything else, our team is one phone call away."
            />
            <Reveal delay={0.2}>
              <div className="mt-10">
                <GhostButton href="/faq" testId="faq-view-all-link">
                  All FAQs <ArrowRight className="h-4 w-4" />
                </GhostButton>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="w-full" data-testid="home-faq-accordion">
              {FAQS.slice(0, 4).map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger
                    data-testid={`home-faq-trigger-${i}`}
                    className="py-6 text-left font-serif text-lg text-white transition-colors duration-300 hover:text-gold hover:no-underline"
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-sm leading-relaxed text-zinc-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-28 md:py-40" data-testid="home-cta">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:px-10 lg:grid-cols-2">
          <div>
            <SectionHeading
              kicker="Request Assistance"
              title={<>Had an accident? <span className="italic text-gold-gradient">Start here.</span></>}
              description="Send us the basics and a dedicated specialist will call you back — or skip the wait and call us right now."
            />
            <Reveal delay={0.2}>
              <div className="mt-10">
                <GoldButton href={PHONE_LINK} testId="cta-call-button">
                  <Phone className="h-4 w-4" strokeWidth={2} /> Call {PHONE}
                </GoldButton>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <EnquiryForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
